import { type ColumnDef, type SortDirection } from '@oysterjs/ui/data-table';
import { TableHeader } from '@oysterjs/ui/table';

import type { EmailTemplateListFieldsFragment } from '~/api/graphql';
import { FieldReferenceText } from './field-reference-text';
import { TitleCell } from '~/components/list/library-table-cells';
import { RowActionsMenu, type RowAction } from '~/components/row-actions-menu';
import { dateFormatter } from '~/components/table-formatters';

type EmailTemplate = EmailTemplateListFieldsFragment;

export type EmailTemplateSortKey = 'name' | 'subject' | 'edited' | 'usage';

/** A subject line on one line, with each [[field]] as a chip. */
export const SubjectWithFields = ({ subject }: { subject: string }) => (
  <span className="block truncate" title={subject}>
    <FieldReferenceText text={subject} />
  </span>
);

/** usageLast30Days is null when the server couldn't count it. */
export const formatUsageLast30Days = (usageLast30Days: number | null | undefined): string => {
  if (usageLast30Days == null) return 'Not available';
  if (usageLast30Days === 0) return 'Not used yet';
  return String(usageLast30Days);
};

const isUsedLast30Days = (usageLast30Days: number | null | undefined) => !!usageLast30Days;

export const getEmailTemplateColumns = (opts: {
  actionsFor: (template: EmailTemplate) => RowAction[];
  /** Disables every row menu while one of their actions is running. */
  isBusy: boolean;
}): ColumnDef<EmailTemplate, EmailTemplateSortKey>[] => [
  {
    id: 'name',
    header: 'Name',
    sortKey: 'name',
    // On a narrow screen the name fills what the row menu leaves and truncates, rather than
    // pushing the menu off the screen.
    cellClassName: 'w-full max-w-0 sm:w-auto sm:max-w-72',
    renderCell: (template) => (
      <>
        <TitleCell title={template.name} visibility={template.visibility} />
        {/* Narrow screens drop the Subject column, so the subject moves under the name. */}
        <span className="mt-1 block text-sm text-neutral-600 sm:hidden dark:text-neutral-400">
          <SubjectWithFields subject={template.subject} />
        </span>
      </>
    )
  },
  {
    id: 'subject',
    header: 'Subject line',
    sortKey: 'subject',
    headerClassName: 'hidden sm:table-cell',
    cellClassName: 'hidden sm:table-cell max-w-md text-sm text-neutral-600 dark:text-neutral-400',
    renderCell: (template) => <SubjectWithFields subject={template.subject} />
  },
  {
    id: 'edited',
    header: 'Last edited',
    sortKey: 'edited',
    headerClassName: 'hidden sm:table-cell',
    cellClassName: 'hidden sm:table-cell whitespace-nowrap',
    renderCell: (template) => (
      <>
        <span className="block text-sm text-neutral-600 dark:text-neutral-400">
          {dateFormatter.format(new Date(template.updatedAt))}
        </span>
        {template.updatedBy && (
          <span className="block text-xs text-neutral-500 dark:text-neutral-400">
            {template.updatedBy.name}
          </span>
        )}
      </>
    )
  },
  {
    id: 'usage',
    header: 'Used, last 30 days',
    sortKey: 'usage',
    headerClassName: 'hidden sm:table-cell',
    cellClassName: 'hidden sm:table-cell whitespace-nowrap',
    renderCell: (template) => (
      <span
        className={
          isUsedLast30Days(template.usageLast30Days)
            ? 'text-sm text-neutral-900 dark:text-white'
            : 'text-sm text-neutral-500 dark:text-neutral-400'
        }
      >
        {formatUsageLast30Days(template.usageLast30Days)}
      </span>
    )
  },
  {
    id: 'actions',
    // A header cell of its own, so the header's divider runs under this column too.
    renderHeader: () => (
      <TableHeader className="w-0 py-1.5 px-3 text-xs">
        <span className="sr-only">Actions</span>
      </TableHeader>
    ),
    noHref: true,
    cellClassName: 'w-0 py-1.5 px-3',
    renderCell: (template) => {
      const actions = opts.actionsFor(template);
      return actions.length > 0 ? (
        <RowActionsMenu
          label={`More actions for ${template.name}`}
          actions={actions}
          disabled={opts.isBusy}
        />
      ) : null;
    }
  }
];

// A count the server couldn't read sorts below every real count, zero included.
const UNKNOWN_USAGE_SORT_VALUE = -1;

const compareEmailTemplates: Record<
  EmailTemplateSortKey,
  (a: EmailTemplate, b: EmailTemplate) => number
> = {
  name: (a, b) => a.name.localeCompare(b.name),
  subject: (a, b) => a.subject.localeCompare(b.subject),
  edited: (a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
  usage: (a, b) =>
    (a.usageLast30Days ?? UNKNOWN_USAGE_SORT_VALUE) -
    (b.usageLast30Days ?? UNKNOWN_USAGE_SORT_VALUE)
};

export const sortEmailTemplates = (
  templates: readonly EmailTemplate[],
  sortKey: EmailTemplateSortKey,
  direction: SortDirection
): EmailTemplate[] => {
  const sorted = [...templates].sort(compareEmailTemplates[sortKey]);
  return direction === 'desc' ? sorted.reverse() : sorted;
};
